import { useCallback, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import AiPlanActiveSwitch from "../AiPlanActiveSwitch";
import {
  DataGridPremium,
  type GridColDef,
  type GridColumnVisibilityModel,
  type GridPaginationModel,
} from "@mui/x-data-grid-premium";
import { useTheme } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";
import {
  aiPlanDataGridPanelSx,
  getDataGridEmptyStateSx,
  getRegulationsListDataGridSx,
} from "../../../components/shared/regulationsListDataGridSx";
import styles from "../../../components/RegulationsTable/RegulationsTable.module.css";
import noDataAnim from "../../../assets/no-data-anim.webp";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useHasPrivilege } from "../../../hooks/useHasPrivilege";
import {
  AUTO_TAGGING_ACTIVATE_SUCCESS_TOAST,
  AUTO_TAGGING_DEACTIVATE_SUCCESS_TOAST,
  deleteAutoTaggingPlan,
  updateAutoTaggingPlanActive,
} from "../../../services/autoTaggingService";
import { AUTO_TAGGING_PRIVILEGES } from "../../../services/permissionsService";
import type { TaggingPlanRow } from "../constants";
import { DataGridManageColumnsPanel } from "../../../components/shared/DataGridManageColumnsPanel";
import DeleteTaggingPlanConfirmDialog from "./DeleteTaggingPlanConfirmDialog";
import EditAssessmentPlanItemDialog from "./EditAssessmentPlanItemDialog";
import TaggingPlanActiveConfirmDialog, {
  type TaggingPlanActiveConfirmMode,
} from "./TaggingPlanActiveConfirmDialog";
import TaggingPlanRowActions from "./TaggingPlanRowActions";
import TaggingPlanTableToolbar from "./TaggingPlanTableToolbar";

type TaggingPlanTableProps = Readonly<{
  rows: TaggingPlanRow[];
  rowCount: number;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  onAddNew?: () => void;
  onPlanDeleted?: () => void;
  onPlanUpdated?: () => void;
}>;

function TaggingPlanNoRowsOverlay({ onAddNew }: { onAddNew?: () => void }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%",
        minHeight: 280,
        py: 4,
        boxSizing: "border-box",
        bgcolor: "background.paper",
      }}
    >
      <Box
        component="img"
        src={noDataAnim}
        alt=""
        sx={{ width: 160, height: "auto", mb: 2 }}
      />
      <Typography sx={{ color: "#8c8c8c", fontSize: "14px" }}>
        No data available.{" "}
        {onAddNew ? (
          <Link
            component="button"
            type="button"
            underline="hover"
            onClick={onAddNew}
            sx={{
              color: "#295EF1",
              fontSize: "14px",
              fontWeight: 500,
              verticalAlign: "baseline",
              cursor: "pointer",
            }}
          >
            Add Now
          </Link>
        ) : null}
      </Typography>
    </Box>
  );
}

export default function TaggingPlanTable({
  rows,
  rowCount,
  paginationModel,
  onPaginationModelChange,
  onAddNew,
  onPlanDeleted,
  onPlanUpdated,
}: TaggingPlanTableProps) {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const { showMessage } = useSnackbar();
  const canManageAutoTagging = useHasPrivilege(AUTO_TAGGING_PRIVILEGES.manage);
  const [deleteTarget, setDeleteTarget] = useState<TaggingPlanRow | null>(null);
  const [editTarget, setEditTarget] = useState<TaggingPlanRow | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [activeToggleTarget, setActiveToggleTarget] = useState<{
    row: TaggingPlanRow;
    nextActive: boolean;
  } | null>(null);
  const [updatingActive, setUpdatingActive] = useState(false);
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({});
  const manageColumnsPanelScrollTopRef = useRef(0);

  const columnsManagementSlot = useCallback(
    () => (
      <DataGridManageColumnsPanel
        columnVisibilityModel={columnVisibilityModel}
        setColumnVisibilityModel={setColumnVisibilityModel}
        scrollTopRef={manageColumnsPanelScrollTopRef}
      />
    ),
    [columnVisibilityModel],
  );

  const noRowsOverlaySlot = useCallback(
    () => (
      <TaggingPlanNoRowsOverlay
        onAddNew={canManageAutoTagging ? onAddNew : undefined}
      />
    ),
    [canManageAutoTagging, onAddNew],
  );

  const handleDeleteRequest = useCallback((row: TaggingPlanRow) => {
    setDeleteTarget(row);
  }, []);

  const handleEditRequest = useCallback((row: TaggingPlanRow) => {
    setEditTarget(row);
  }, []);

  const handleCloseEditDialog = useCallback(() => {
    if (savingEdit) return;
    setEditTarget(null);
  }, [savingEdit]);

  const handleEditSaved = useCallback(() => {
    setEditTarget(null);
    onPlanUpdated?.();
  }, [onPlanUpdated]);

  const handleCloseDeleteDialog = useCallback(() => {
    if (deleting) return;
    setDeleteTarget(null);
  }, [deleting]);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget || deleting) return;

    setDeleting(true);
    try {
      await deleteAutoTaggingPlan(deleteTarget.id);
      showMessage("Tagging plan deleted successfully.", "success");
      setDeleteTarget(null);
      onPlanDeleted?.();
    } catch (error) {
      showMessage(
        error instanceof Error ? error.message : "Failed to delete tagging plan.",
        "error",
      );
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, deleting, onPlanDeleted, showMessage]);

  const activeConfirmMode: TaggingPlanActiveConfirmMode | null = activeToggleTarget
    ? activeToggleTarget.nextActive
      ? "activate"
      : "deactivate"
    : null;

  const handleActiveToggleRequest = useCallback(
    (row: TaggingPlanRow, nextActive: boolean) => {
      setActiveToggleTarget({ row, nextActive });
    },
    [],
  );

  const handleCloseActiveDialog = useCallback(() => {
    if (updatingActive) return;
    setActiveToggleTarget(null);
  }, [updatingActive]);

  const handleConfirmActiveToggle = useCallback(async () => {
    if (!activeToggleTarget || updatingActive) return;

    const { row, nextActive } = activeToggleTarget;
    setUpdatingActive(true);
    try {
      await updateAutoTaggingPlanActive(row.id, nextActive);
      showMessage(
        nextActive
          ? AUTO_TAGGING_ACTIVATE_SUCCESS_TOAST
          : AUTO_TAGGING_DEACTIVATE_SUCCESS_TOAST,
        "success",
      );
      setActiveToggleTarget(null);
      onPlanUpdated?.();
    } catch (error) {
      showMessage(
        error instanceof Error
          ? error.message
          : "Failed to update tagging plan status.",
        "error",
      );
    } finally {
      setUpdatingActive(false);
    }
  }, [activeToggleTarget, onPlanUpdated, showMessage, updatingActive]);

  const columns = useMemo<GridColDef<TaggingPlanRow>[]>(
    () => [
      {
        field: "id",
        headerName: "ID",
        width: 100,
        sortable: false,
        filterable: false,
      },
      {
        field: "objectTypeName",
        headerName: "Object Type",
        width: 140,
        sortable: false,
        filterable: false,
      },
      {
        field: "objectSubTypeName",
        headerName: "Object Sub-Type",
        width: 160,
        sortable: false,
        filterable: false,
        valueGetter: (_value, row) => row.objectSubTypeName?.trim() || "-",
        renderCell: ({ value }) => {
          const text = typeof value === "string" ? value.trim() : "";
          const isEmpty = !text || text === "-";
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
                width: "100%",
              }}
            >
              <Typography
                noWrap
                sx={{
                  color: isEmpty ? "#8c8c8c" : "inherit",
                  fontSize: "14px",
                }}
              >
                {isEmpty ? "-" : text}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: "concept",
        headerName: "Concept",
        width: 180,
        sortable: false,
        filterable: false,
      },
      {
        field: "values",
        headerName: "Values",
        flex: 1,
        minWidth: 220,
        sortable: false,
        filterable: false,
      },
      {
        field: "description",
        headerName: "Assessment Prompt",
        flex: 1.2,
        minWidth: 280,
        sortable: false,
        filterable: false,
        cellClassName: "ai-plan-description-cell",
        renderCell: ({ value }) => {
          const text = typeof value === "string" ? value.trim() : "";
          const isEmpty = !text;
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
                width: "100%",
                minWidth: 0,
                minHeight: 0,
                boxSizing: "border-box",
              }}
            >
              <Typography
                noWrap
                title={isEmpty ? undefined : text}
                sx={{
                  color: isEmpty ? "#8c8c8c" : "inherit",
                  fontSize: "14px",
                  width: "100%",
                  maxWidth: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  lineHeight: 1.4,
                }}
              >
                {isEmpty ? "-" : text}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: "enabled",
        headerName: "Active",
        width: 88,
        sortable: false,
        filterable: false,
        resizable: false,
        disableExport: true,
        align: "center",
        headerAlign: "center",
        cellClassName: "ai-plan-status-cell",
        renderCell: ({ row }) => (
          <AiPlanActiveSwitch
            checked={row.enabled}
            disabled={!canManageAutoTagging}
            onChange={(nextChecked) =>
              handleActiveToggleRequest(row, nextChecked)
            }
          />
        ),
      },
      ...(canManageAutoTagging
        ? [
            {
              field: "actions",
              headerName: "Actions",
              width: 88,
              sortable: false,
              filterable: false,
              resizable: false,
              disableExport: true,
              align: "center" as const,
              headerAlign: "center" as const,
              cellClassName: "ai-plan-actions-cell",
              renderCell: ({ row }: { row: TaggingPlanRow }) => (
                <TaggingPlanRowActions
                  onEdit={() => handleEditRequest(row)}
                  onDelete={() => handleDeleteRequest(row)}
                />
              ),
            } satisfies GridColDef<TaggingPlanRow>,
          ]
        : []),
    ],
    [
      canManageAutoTagging,
      handleActiveToggleRequest,
      handleDeleteRequest,
      handleEditRequest,
    ],
  );

  const isEmpty = rows.length === 0;

  return (
    <>
      <Box
      className={`${styles.dataGridPanel}${
        isEmpty ? ` ${styles.dataGridPanelEmpty}` : ""
      }`}
      sx={{
        ...aiPlanDataGridPanelSx,
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 480,
      }}
    >
      <DataGridPremium
        rows={rows}
        columns={columns}
        getRowId={(row) => row.id}
        rowHeight={48}
        columnHeaderHeight={48}
        disableRowSelectionOnClick
        disableRowGrouping
        disableAggregation
        disablePivoting
        disableColumnSorting
        pagination
        paginationMode="server"
        rowCount={rowCount}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        pageSizeOptions={[5, 10, 20, 50]}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
        showToolbar
        showColumnVerticalBorder={false}
        disableColumnPinning={false}
        pinnedColumnsSectionSeparator="border"
        slots={{
          toolbar: TaggingPlanTableToolbar,
          noRowsOverlay: noRowsOverlaySlot,
          columnsManagement: columnsManagementSlot,
        }}
        slotProps={{
          toolbar: {},
        }}
        initialState={{
          pinnedColumns: {
            right: canManageAutoTagging ? ["enabled", "actions"] : ["enabled"],
          },
        }}
        sx={
          [
            getRegulationsListDataGridSx(primaryColor),
            ...(isEmpty ? [getDataGridEmptyStateSx()] : []),
            {
              height: "100%",
              minHeight: 400,
              "& .ai-plan-status-cell, & .ai-plan-actions-cell": {
                display: "flex !important",
                alignItems: "center !important",
                justifyContent: "center !important",
                "& .MuiDataGrid-cellContent": {
                  display: "flex !important",
                  alignItems: "center !important",
                  justifyContent: "center !important",
                  width: "100%",
                  height: "100%",
                  overflow: "visible",
                },
              },
              "& .ai-plan-actions-cell.MuiDataGrid-cell--pinnedRight": {
                boxShadow: "none",
              },
              "& .ai-plan-status-cell.MuiDataGrid-cell--pinnedRight": {
                boxShadow: "none",
              },
            },
          ] as SxProps<Theme>
        }
      />
      </Box>
      <DeleteTaggingPlanConfirmDialog
        open={deleteTarget !== null}
        deleting={deleting}
        onClose={handleCloseDeleteDialog}
        onConfirm={() => void handleConfirmDelete()}
      />
      <EditAssessmentPlanItemDialog
        open={editTarget !== null}
        plan={editTarget}
        saving={savingEdit}
        onClose={handleCloseEditDialog}
        onSave={handleEditSaved}
        onSavingChange={setSavingEdit}
        onSuccess={(message) => showMessage(message, "success")}
        onError={(message) => showMessage(message, "error")}
      />
      <TaggingPlanActiveConfirmDialog
        open={activeToggleTarget !== null}
        mode={activeConfirmMode}
        saving={updatingActive}
        onClose={handleCloseActiveDialog}
        onConfirm={() => void handleConfirmActiveToggle()}
      />
    </>
  );
}
